import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  pickWeightedKey,
  recordAttempt,
} from "@nav-games/games-core";
import { CHARTWORK_IDS, CHARTWORK_SYMBOLS } from "@nav-games/chartwork";
import {
  NATO_CONFUSERS,
  NATO_DIGITS,
  NATO_KEYS,
  NATO_LETTERS,
} from "@nav-games/comms";
import {
  DISTRESS_IDS,
  DISTRESS_SIGNALS,
} from "@nav-games/distress";
import {
  COMBO_IDS,
  ICS_COMBOS,
  ICS_FLAGS,
} from "@nav-games/flags";
import { IALA_IDS, IALA_MARKS } from "@nav-games/iala";
import { ITU_CODE } from "@nav-games/morse";
import {
  NAV_LIGHTS,
  VESSEL_IDS,
} from "@nav-games/nav-lights";
import { SEMAPHORE_CODE } from "@nav-games/semaphore";
import {
  WEATHER_TERMS,
} from "@nav-games/weather";

import { ChartworkSymbol } from "../../ui/ChartworkSymbol";
import { DayShapeFigure } from "../../ui/DayShapeFigure";
import { Flag } from "../../ui/Flag";
import { FlagHoist } from "../../ui/FlagHoist";
import { BuoyFigure } from "../../ui/BuoyFigure";
import { NavLightsFigure } from "../../ui/NavLightsFigure";
import { SemaphoreFigure } from "../../ui/SemaphoreFigure";

interface ExamProps {
  onNavigate: (to: string) => void;
}

const QUESTIONS_PER_EXAM = 20;
const TIME_LIMIT_MS = 0; // no time limit by default

interface Choice {
  id: string;
  label: React.ReactNode;
  correct: boolean;
}

interface Question {
  /// Module the question is drawn from — also used as stats key.
  module: string;
  /// Short label shown above the question card so the player knows
  /// which catalogue this came from.
  moduleLabel: string;
  /// Stable identifier of the answer (used to record stats).
  answerKey: string;
  /// Prompt rendered above the choices.
  prompt: React.ReactNode;
  /// Multiple-choice options. Exactly one must have correct: true.
  choices: readonly Choice[];
  /// Plain-English explanation shown on reveal.
  explanation: string;
}

type QuestionGenerator = () => Question;

// ─── Question generators ────────────────────────────────────────

const ITU_KEYS = Object.keys(ITU_CODE).filter(
  (k) => /^[A-Z0-9]$/.test(k),
);

const SEMAPHORE_KEYS = Object.keys(SEMAPHORE_CODE);

const FLAG_KEYS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const WEATHER_KEYS = Object.keys(WEATHER_TERMS);

const DAY_SHAPE_IDS = VESSEL_IDS.filter(
  (id) => NAV_LIGHTS[id]!.dayShapes.length > 0,
);

function shuffle<T>(input: readonly T[]): T[] {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const ai = arr[i]!;
    const aj = arr[j]!;
    arr[i] = aj;
    arr[j] = ai;
  }
  return arr;
}

function pickRandom<T>(pool: readonly T[]): T {
  return pool[Math.floor(Math.random() * pool.length)]!;
}

function distractorKeys(
  pool: readonly string[],
  answer: string,
  n: number,
): string[] {
  const distractors: string[] = [];
  const taken = new Set([answer]);
  while (distractors.length < n) {
    if (taken.size >= pool.length) break;
    const k = pickRandom(pool);
    if (!taken.has(k)) {
      taken.add(k);
      distractors.push(k);
    }
  }
  return distractors;
}

function makeChoices(
  correctId: string,
  correctLabel: React.ReactNode,
  decoyIds: readonly string[],
  decoyLabel: (id: string) => React.ReactNode,
): Choice[] {
  return shuffle<Choice>([
    { id: correctId, label: correctLabel, correct: true },
    ...decoyIds.map((id) => ({
      id,
      label: decoyLabel(id),
      correct: false,
    })),
  ]);
}

// Per-submodule recent-answer buffers, so the same item isn't drawn
// twice within one exam. `pickExam` wraps the shared spaced-repetition
// picker with a recent-exclusion window (the picker repeats freely
// otherwise); `resetExamRecents` clears the buffers at the start of
// each exam.
const examRecents = new Map<string, string[]>();

function pickExam(module: string, pool: readonly string[]): string {
  const recent = examRecents.get(module) ?? [];
  const key = pickWeightedKey(module, pool, recent);
  const window = Math.min(8, pool.length - 1);
  examRecents.set(module, [...recent, key].slice(-window));
  return key;
}

function resetExamRecents(): void {
  examRecents.clear();
}

const morseLetter: QuestionGenerator = () => {
  const answer = pickExam("exam-morse", ITU_KEYS);
  const pattern = ITU_CODE[answer]!.replace(/\./g, "•").replace(/-/g, "—");
  const decoyIds = distractorKeys(ITU_KEYS, answer, 3);
  return {
    module: "exam-morse",
    moduleLabel: "Morse",
    answerKey: answer,
    prompt: (
      <div
        style={{
          fontSize: 36,
          textAlign: "center",
          letterSpacing: "0.15em",
          fontFamily: "ui-monospace, monospace",
        }}
      >
        {pattern}
      </div>
    ),
    choices: makeChoices(
      answer,
      <span style={{ fontSize: 24, fontWeight: 700 }}>{answer}</span>,
      decoyIds,
      (id) => <span style={{ fontSize: 24, fontWeight: 700 }}>{id}</span>,
    ),
    explanation: `${answer} = ${pattern}`,
  };
};

const natoWord: QuestionGenerator = () => {
  const answer = pickExam("exam-nato", NATO_KEYS);
  const word = (NATO_LETTERS[answer] ?? NATO_DIGITS[answer])!.word;
  // Use confusion words as decoys when available.
  const confusers = NATO_CONFUSERS[answer] ?? [];
  const decoys = shuffle(confusers).slice(0, 3);
  while (decoys.length < 3) {
    const k = pickRandom(NATO_KEYS);
    const w = (NATO_LETTERS[k] ?? NATO_DIGITS[k])!.word;
    if (w !== word && !decoys.includes(w)) decoys.push(w);
  }
  return {
    module: "exam-nato",
    moduleLabel: "NATO phonetic",
    answerKey: answer,
    prompt: (
      <div
        style={{
          fontSize: 48,
          textAlign: "center",
          fontWeight: 700,
        }}
      >
        {answer}
      </div>
    ),
    choices: shuffle<Choice>([
      { id: word, label: word, correct: true },
      ...decoys.map((d) => ({ id: d, label: d, correct: false })),
    ]),
    explanation: `${answer} = ${word}`,
  };
};

const semaphoreLetter: QuestionGenerator = () => {
  const answer = pickExam("exam-semaphore", SEMAPHORE_KEYS);
  const decoyIds = distractorKeys(SEMAPHORE_KEYS, answer, 3);
  return {
    module: "exam-semaphore",
    moduleLabel: "Semaphore",
    answerKey: answer,
    prompt: (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <SemaphoreFigure pose={SEMAPHORE_CODE[answer]!} size={140} />
      </div>
    ),
    choices: makeChoices(
      answer,
      <span style={{ fontSize: 22, fontWeight: 700 }}>{answer}</span>,
      decoyIds,
      (id) => <span style={{ fontSize: 22, fontWeight: 700 }}>{id}</span>,
    ),
    explanation: `Pose = letter ${answer}`,
  };
};

const flagLetter: QuestionGenerator = () => {
  const answer = pickExam("exam-flag", FLAG_KEYS);
  const decoyIds = distractorKeys(FLAG_KEYS, answer, 3);
  return {
    module: "exam-flag",
    moduleLabel: "ICS flag",
    answerKey: answer,
    prompt: (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Flag char={answer} size={120} />
      </div>
    ),
    choices: makeChoices(
      answer,
      <span style={{ fontSize: 22, fontWeight: 700 }}>
        {answer} — {ICS_FLAGS[answer]?.name}
      </span>,
      decoyIds,
      (id) => (
        <span style={{ fontSize: 16 }}>
          {id} — {ICS_FLAGS[id]?.name}
        </span>
      ),
    ),
    explanation: `${answer} = ${ICS_FLAGS[answer]?.name}`,
  };
};

const comboMeaning: QuestionGenerator = () => {
  const answer = pickExam("exam-combo", COMBO_IDS);
  const decoyIds = distractorKeys(COMBO_IDS, answer, 3);
  return {
    module: "exam-combo",
    moduleLabel: "Two-flag combo",
    answerKey: answer,
    prompt: (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <FlagHoist flags={ICS_COMBOS[answer]!.flags} size={56} />
      </div>
    ),
    choices: makeChoices(
      answer,
      <span style={{ fontSize: 14 }}>
        {answer} — {ICS_COMBOS[answer]?.shortMeaning}
      </span>,
      decoyIds,
      (id) => (
        <span style={{ fontSize: 14 }}>
          {id} — {ICS_COMBOS[id]?.shortMeaning}
        </span>
      ),
    ),
    explanation: `${answer}: ${ICS_COMBOS[answer]?.meaning}`,
  };
};

const navLightVessel: QuestionGenerator = () => {
  const answer = pickExam("exam-lights", VESSEL_IDS);
  const decoyIds = distractorKeys(VESSEL_IDS, answer, 3);
  return {
    module: "exam-lights",
    moduleLabel: "Nav lights",
    answerKey: answer,
    prompt: (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <NavLightsFigure
          vessel={NAV_LIGHTS[answer]!}
          size={180}
          silhouetteHidden
        />
      </div>
    ),
    choices: makeChoices(
      answer,
      <span style={{ fontSize: 14 }}>{NAV_LIGHTS[answer]!.name}</span>,
      decoyIds,
      (id) => <span style={{ fontSize: 14 }}>{NAV_LIGHTS[id]?.name ?? id}</span>,
    ),
    explanation: `${NAV_LIGHTS[answer]!.name} — ${NAV_LIGHTS[answer]!.rule}`,
  };
};

const dayShapeVessel: QuestionGenerator = () => {
  const answer = pickExam("exam-day-shapes", DAY_SHAPE_IDS);
  const decoyIds = distractorKeys(DAY_SHAPE_IDS, answer, 3);
  return {
    module: "exam-day-shapes",
    moduleLabel: "Day shapes",
    answerKey: answer,
    prompt: (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <DayShapeFigure shapes={NAV_LIGHTS[answer]!.dayShapes} size={140} />
      </div>
    ),
    choices: makeChoices(
      answer,
      <span style={{ fontSize: 14 }}>{NAV_LIGHTS[answer]!.name}</span>,
      decoyIds,
      (id) => <span style={{ fontSize: 14 }}>{NAV_LIGHTS[id]?.name ?? id}</span>,
    ),
    explanation: `${NAV_LIGHTS[answer]!.name} — ${NAV_LIGHTS[answer]!.dayShapeRule}`,
  };
};

const buoyMeaning: QuestionGenerator = () => {
  const answer = pickExam("exam-iala", IALA_IDS);
  const decoyIds = distractorKeys(IALA_IDS, answer, 3);
  const region: "A" | "B" = Math.random() < 0.5 ? "A" : "B";
  return {
    module: "exam-iala",
    moduleLabel: `IALA Region ${region}`,
    answerKey: answer,
    prompt: (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <BuoyFigure mark={IALA_MARKS[answer]!} region={region} size={140} />
      </div>
    ),
    choices: makeChoices(
      answer,
      <span style={{ fontSize: 13 }}>{IALA_MARKS[answer]?.shortMeaning}</span>,
      decoyIds,
      (id) => (
        <span style={{ fontSize: 13 }}>{IALA_MARKS[id]?.shortMeaning ?? id}</span>
      ),
    ),
    explanation: `${IALA_MARKS[answer]!.name} — ${IALA_MARKS[answer]!.meaning}`,
  };
};

const distressSignal: QuestionGenerator = () => {
  const answer = pickExam("exam-distress", DISTRESS_IDS);
  const decoyIds = distractorKeys(DISTRESS_IDS, answer, 3);
  return {
    module: "exam-distress",
    moduleLabel: "Distress signal",
    answerKey: answer,
    prompt: (
      <p style={{ fontSize: 16, margin: "8px 0 16px" }}>
        {DISTRESS_SIGNALS[answer]!.meaning}
      </p>
    ),
    choices: makeChoices(
      answer,
      <span style={{ fontSize: 14 }}>{DISTRESS_SIGNALS[answer]!.name}</span>,
      decoyIds,
      (id) => (
        <span style={{ fontSize: 14 }}>{DISTRESS_SIGNALS[id]?.name ?? id}</span>
      ),
    ),
    explanation: `${DISTRESS_SIGNALS[answer]!.name} — ${DISTRESS_SIGNALS[answer]!.use}`,
  };
};

const chartworkSymbol: QuestionGenerator = () => {
  const answer = pickExam("exam-chartwork", CHARTWORK_IDS);
  const decoyIds = distractorKeys(CHARTWORK_IDS, answer, 3);
  return {
    module: "exam-chartwork",
    moduleLabel: "Chartwork",
    answerKey: answer,
    prompt: (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <ChartworkSymbol symbol={CHARTWORK_SYMBOLS[answer]!} size={140} />
      </div>
    ),
    choices: makeChoices(
      answer,
      <span style={{ fontSize: 14 }}>{CHARTWORK_SYMBOLS[answer]!.name}</span>,
      decoyIds,
      (id) => (
        <span style={{ fontSize: 14 }}>
          {CHARTWORK_SYMBOLS[id]?.name ?? id}
        </span>
      ),
    ),
    explanation: `${CHARTWORK_SYMBOLS[answer]!.name} — ${CHARTWORK_SYMBOLS[answer]!.meaning}`,
  };
};

const weatherTerm: QuestionGenerator = () => {
  const answer = pickExam("exam-weather", WEATHER_KEYS);
  const decoyIds = distractorKeys(WEATHER_KEYS, answer, 3);
  return {
    module: "exam-weather",
    moduleLabel: "Met Office term",
    answerKey: answer,
    prompt: (
      <p
        style={{
          fontSize: 28,
          textAlign: "center",
          margin: "16px 0 24px",
          fontWeight: 600,
        }}
      >
        {WEATHER_TERMS[answer]!.term}
      </p>
    ),
    choices: makeChoices(
      answer,
      <span style={{ fontSize: 14 }}>{WEATHER_TERMS[answer]!.shortMeaning}</span>,
      decoyIds,
      (id) => (
        <span style={{ fontSize: 14 }}>
          {WEATHER_TERMS[id]?.shortMeaning ?? id}
        </span>
      ),
    ),
    explanation: `${WEATHER_TERMS[answer]!.term}: ${WEATHER_TERMS[answer]!.meaning ?? WEATHER_TERMS[answer]!.shortMeaning}`,
  };
};

const GENERATORS: readonly QuestionGenerator[] = [
  morseLetter,
  natoWord,
  semaphoreLetter,
  flagLetter,
  comboMeaning,
  navLightVessel,
  dayShapeVessel,
  buoyMeaning,
  distressSignal,
  chartworkSymbol,
  weatherTerm,
];

function nextQuestion(): Question {
  return GENERATORS[Math.floor(Math.random() * GENERATORS.length)]!();
}

// ─── Page ───────────────────────────────────────────────────────

type Stage = "picker" | "running" | "ended";

export function Exam({ onNavigate }: ExamProps) {
  const [stage, setStage] = useState<Stage>("picker");

  if (stage === "picker") {
    return (
      <>
        <h2 style={{ marginTop: 0 }}>Mixed exam</h2>
        <p className="muted">
          {QUESTIONS_PER_EXAM} questions drawn at random across every
          flashcards module in the app — Morse, semaphore, ICS flags,
          two-flag combinations, nav lights, day shapes, IALA buoyage
          (random region per card), distress signals, chartwork,
          weather, NATO phonetic. Simulates the recognition portion
          of an RYA shore-based exam.
        </p>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>How it works</h3>
          <ul className="small" style={{ paddingLeft: 18 }}>
            <li>
              Each question is multiple choice with one correct
              answer.
            </li>
            <li>
              No time pressure, no lives — answer at your own pace.
            </li>
            <li>
              At the end you'll see your overall score and a per-
              module breakdown showing where to revise.
            </li>
            <li>
              Your answers feed the same spaced-repetition stats as
              the individual flashcards modules, so weak topics
              automatically come back more often in regular drills.
            </li>
          </ul>
        </div>
        <button onClick={() => setStage("running")}>
          Start {QUESTIONS_PER_EXAM}-question exam
        </button>{" "}
        <button className="secondary" onClick={() => onNavigate("/play")}>
          ← Back to Play
        </button>
      </>
    );
  }

  if (stage === "running") {
    return (
      <Running
        onDone={() => setStage("ended")}
        onQuit={() => setStage("picker")}
      />
    );
  }

  return null;
}

function Running({
  onDone: _onDone,
  onQuit,
}: {
  onDone: () => void;
  onQuit: () => void;
}) {
  // Tracking per-module results so the end card can break down the
  // score by topic.
  const recorderRef = useRef<SessionRecorder>(
    new SessionRecorder({
      module: "exam",
      game: "mixed",
      length: QUESTIONS_PER_EXAM,
    }),
  );

  const [index, setIndex] = useState(0);
  const [question, setQuestion] = useState<Question>(() => {
    // Fresh exam — clear the per-submodule recent buffers so the
    // repeat-exclusion window starts empty.
    resetExamRecents();
    return nextQuestion();
  });
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [feedback, setFeedback] = useState<"right" | "wrong" | null>(null);
  const [chosenId, setChosenId] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [perModule, setPerModule] = useState<
    Record<string, { label: string; correct: number; total: number }>
  >({});
  const sessionStart = useRef<number>(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= QUESTIONS_PER_EXAM) {
      setResult(recorderRef.current.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setQuestion(nextQuestion());
    setShownAt(Date.now());
    setFeedback(null);
    setChosenId(null);
  }, [index]);

  const onChoose = useCallback(
    (choice: Choice) => {
      if (feedback || result) return;
      const ms = Date.now() - shownAt;
      const ok = choice.correct;
      const attempt: Omit<Attempt, "module"> = {
        char: question.answerKey,
        ok,
        ms,
        ts: Date.now(),
      };
      recorderRef.current.log(attempt);
      // Record against both the exam-module key (so the exam itself
      // accumulates stats) AND the underlying module-specific key
      // so the individual flashcards modules see the weighting.
      recordAttempt({ ...attempt, module: question.module });
      setPerModule((cur) => {
        const k = question.module;
        const prev = cur[k] ?? { label: question.moduleLabel, correct: 0, total: 0 };
        return {
          ...cur,
          [k]: {
            label: question.moduleLabel,
            correct: prev.correct + (ok ? 1 : 0),
            total: prev.total + 1,
          },
        };
      });
      setChosenId(choice.id);
      setFeedback(ok ? "right" : "wrong");
    },
    [feedback, question, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        elapsedS={Math.round((Date.now() - sessionStart.current) / 1000)}
        perModule={perModule}
        onAgain={() => {
          setIndex(0);
          setQuestion(nextQuestion());
          setShownAt(Date.now());
          setFeedback(null);
          setChosenId(null);
          setResult(null);
          setPerModule({});
          recorderRef.current = new SessionRecorder({
            module: "exam",
            game: "mixed",
            length: QUESTIONS_PER_EXAM,
          });
          sessionStart.current = Date.now();
        }}
        onBack={onQuit}
      />
    );
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
        <h2 style={{ margin: 0 }}>Mixed exam</h2>
        <span className="pill">
          Question {index + 1} of {QUESTIONS_PER_EXAM}
        </span>
        <span className="pill">{question.moduleLabel}</span>
      </div>
      <div className="card">
        {question.prompt}
        {feedback && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 12,
              padding: 10,
              borderRadius: 6,
              background:
                feedback === "right"
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {feedback === "right" ? "✓ Correct." : "✗ Not quite."}
            </p>
            <p className="small muted" style={{ margin: "4px 0 0" }}>
              {question.explanation}
            </p>
            <button
              onClick={advance}
              style={{ marginTop: 10, width: "100%" }}
            >
              {index + 1 >= QUESTIONS_PER_EXAM
                ? "See score →"
                : "Next question →"}
            </button>
          </div>
        )}
        {!feedback && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
              marginTop: 16,
            }}
          >
            {question.choices.map((c) => (
              <button
                key={c.id}
                className="secondary"
                onClick={() => onChoose(c)}
                disabled={chosenId !== null}
                style={{
                  fontSize: 14,
                  padding: "12px 8px",
                  textAlign: "left",
                  minHeight: 56,
                }}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <button
        className="secondary"
        onClick={onQuit}
        style={{ marginTop: 12 }}
      >
        Quit exam
      </button>
    </>
  );
}

function EndCard({
  result,
  elapsedS,
  perModule,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  elapsedS: number;
  perModule: Record<string, { label: string; correct: number; total: number }>;
  onAgain: () => void;
  onBack: () => void;
}) {
  const sortedModules = Object.values(perModule).sort(
    (a, b) => a.correct / a.total - b.correct / b.total,
  );
  const pct = result.total > 0
    ? Math.round((result.correct / result.total) * 100)
    : 0;
  return (
    <>
      <h2>Exam complete</h2>
      <div className="card">
        <p
          style={{
            fontSize: 22,
            margin: "0 0 4px",
            fontWeight: 700,
            color:
              pct >= 80
                ? "var(--success)"
                : pct >= 60
                  ? "var(--warn)"
                  : "var(--danger)",
          }}
        >
          {result.correct} / {result.total} ({pct}%)
        </p>
        <p className="small muted" style={{ margin: 0 }}>
          Elapsed: {elapsedS}s · Average{" "}
          {Math.round(elapsedS / Math.max(1, result.total))}s per question
        </p>
      </div>

      {sortedModules.length > 0 && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>By topic — weakest first</h3>
          <table style={{ width: "100%", fontSize: 14 }}>
            <tbody>
              {sortedModules.map((m) => {
                const mpct = Math.round((m.correct / m.total) * 100);
                return (
                  <tr key={m.label}>
                    <td style={{ padding: "4px 8px 4px 0" }}>{m.label}</td>
                    <td style={{ padding: "4px 8px", width: 90 }}>
                      {m.correct}/{m.total}
                    </td>
                    <td
                      className="small"
                      style={{
                        padding: "4px 8px",
                        color:
                          mpct >= 80
                            ? "var(--success)"
                            : mpct >= 60
                              ? "var(--warn)"
                              : "var(--danger)",
                        fontWeight: 600,
                      }}
                    >
                      {mpct}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="small muted" style={{ margin: "8px 0 0" }}>
            Topics scored under 60% will surface more often in their
            individual flashcards drills thanks to spaced repetition.
          </p>
        </div>
      )}

      <button onClick={onAgain}>New exam</button>{" "}
      <button className="secondary" onClick={onBack}>
        ← Back to Play
      </button>
    </>
  );
}

// silence "unused" warning on the time-limit constant; will be
// exercised once timed-mode is added.
void TIME_LIMIT_MS;
